import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MarkdownText } from '@/components/common/MarkdownText';
import { api } from '@/lib/api';
import type { ApiResponse } from '@/types';
import type { MarketingStrategy } from '@/types';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { Bot, ChevronLeft, ChevronRight, Loader2, Sparkles } from 'lucide-react';

export type StrategyMainTab = 'research' | 'strategy' | 'history';

const STEP_IDS = ['confirm', 'research_prepare', 'research_review', 'strategy_refine'] as const;
type StepId = (typeof STEP_IDS)[number];

const STEP_META: Record<
  StepId,
  { title: string; short: string }
> = {
  confirm: { title: 'Ausgangslage', short: 'Start' },
  research_prepare: { title: 'Forschung planen', short: 'Planen' },
  research_review: { title: 'Forschung auswerten', short: 'Auswerten' },
  strategy_refine: { title: 'Strategie ausarbeiten', short: 'Strategie' },
};

function storageKey(strategyId: string) {
  return `lc_strategy_wizard_step_v1_${strategyId}`;
}

function completedResearchCount(strategy: MarketingStrategy): number {
  return (strategy.research_runs ?? []).filter((r) => r.status === 'completed').length;
}

export interface StrategyWizardPanelProps {
  strategy: MarketingStrategy;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mainTab: StrategyMainTab;
  onMainTabChange: (tab: StrategyMainTab) => void;
  onRefreshStrategy: () => void;
}

export function StrategyWizardPanel({
  strategy,
  open,
  onOpenChange,
  mainTab,
  onMainTabChange,
  onRefreshStrategy,
}: StrategyWizardPanelProps) {
  const [stepIndex, setStepIndex] = useState(0);
  const [coachMd, setCoachMd] = useState<string>('');
  const [coachLoading, setCoachLoading] = useState(false);
  const [extraNote, setExtraNote] = useState('');
  const extraNoteRef = useRef(extraNote);
  extraNoteRef.current = extraNote;

  const stepId = STEP_IDS[stepIndex] ?? 'confirm';

  const doneCount = useMemo(() => completedResearchCount(strategy), [strategy]);

  useEffect(() => {
    if (!open) return;
    try {
      const raw = sessionStorage.getItem(storageKey(strategy.id));
      if (raw) {
        const n = parseInt(raw, 10);
        if (!Number.isNaN(n) && n >= 0 && n < STEP_IDS.length) setStepIndex(n);
      }
    } catch {
      /* ignore */
    }
  }, [open, strategy.id]);

  useEffect(() => {
    if (!open) return;
    try {
      sessionStorage.setItem(storageKey(strategy.id), String(stepIndex));
    } catch {
      /* ignore */
    }
  }, [open, strategy.id, stepIndex]);

  const loadCoach = useCallback(
    async (includeUserNote: boolean) => {
      setCoachLoading(true);
      setCoachMd('');
      const note = includeUserNote ? extraNoteRef.current.trim() || undefined : undefined;
      try {
        const res = await api.post<ApiResponse<{ markdown: string }>>(
          `/marketing-strategies/${strategy.id}/wizard/step-coach`,
          {
            step: stepId,
            extra_user_note: note,
          }
        );
        if (res.success && res.data?.markdown) {
          setCoachMd(res.data.markdown);
        } else {
          setCoachMd(`*${res.error ?? 'Coach konnte nicht geladen werden.'}*`);
        }
      } catch (e) {
        setCoachMd(`*${e instanceof Error ? e.message : 'Fehler beim Coach.'}*`);
      } finally {
        setCoachLoading(false);
      }
    },
    [strategy.id, stepId]
  );

  useEffect(() => {
    if (!open) return;
    void loadCoach(false);
  }, [open, stepId, strategy.id, loadCoach]);

  const canGoNext = useMemo(() => {
    if (stepIndex >= STEP_IDS.length - 1) return false;
    if (stepIndex === 2) {
      return doneCount >= 1;
    }
    return true;
  }, [stepIndex, doneCount]);

  const goNext = () => {
    if (stepIndex === 0) {
      setStepIndex(1);
      onMainTabChange('research');
      return;
    }
    if (stepIndex === 1) {
      setStepIndex(2);
      onMainTabChange('research');
      void onRefreshStrategy();
      return;
    }
    if (stepIndex === 2) {
      if (doneCount < 1) {
        toast.error('Bitte mindestens einen Research-Run erfolgreich abschliessen.');
        return;
      }
      setStepIndex(3);
      onMainTabChange('strategy');
      void onRefreshStrategy();
    }
  };

  const goBack = () => {
    if (stepIndex <= 0) return;
    const next = stepIndex - 1;
    setStepIndex(next);
    if (next === 0) return;
    if (next <= 2) onMainTabChange('research');
  };

  const openCompanion = () => {
    const recentQueries = (strategy.research_runs ?? [])
      .slice(0, 8)
      .map((r) => r.query)
      .filter((q) => q && q.trim().length > 0);
    const payload = {
      strategyId: strategy.id,
      title: strategy.title,
      year: strategy.year,
      wizardStep: stepId,
      completedRuns: doneCount,
      recentQueries,
      userNote: extraNote.trim() || undefined,
    };
    try {
      sessionStorage.setItem('lc_strategy_wizard_handoff_v1', JSON.stringify(payload));
    } catch {
      toast.error('Handoff konnte nicht vorbereitet werden.');
      return;
    }
    window.location.hash = '#/companion?wizard=strategy&handoff=1';
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(90vh,880px)] w-full max-w-4xl flex-col gap-0 overflow-hidden p-0 sm:max-w-4xl">
        <DialogHeader className="shrink-0 border-b border-border px-6 py-4">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="h-5 w-5 text-muted" />
            Strategie-Assistent
          </DialogTitle>
          <DialogDescription className="text-left">
            {strategy.title} · {strategy.year} — Schritt {stepIndex + 1} von {STEP_IDS.length}:{' '}
            {STEP_META[stepId].title}
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-3 px-6 py-4">
          <div className="flex w-full flex-wrap gap-1.5">
            {STEP_IDS.map((id, i) => (
              <button
                key={id}
                type="button"
                onClick={() => {
                  const done = completedResearchCount(strategy);
                  if (i >= 2 && done < 1 && i > stepIndex) {
                    toast.message('Zuerst einen Research-Run abschliessen.');
                    return;
                  }
                  if (i === 3 && done < 1) {
                    toast.message('Zuerst einen Research-Run abschliessen.');
                    return;
                  }
                  setStepIndex(i);
                  if (i === 3) onMainTabChange('strategy');
                  else if (i >= 1) onMainTabChange('research');
                }}
                className={cn(
                  'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                  i === stepIndex
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card-hover text-muted hover:bg-border'
                )}
              >
                {i + 1}. {STEP_META[id].short}
              </button>
            ))}
          </div>

          <div className="grid min-h-0 flex-1 gap-3 md:grid-cols-[1fr_220px]">
            <ScrollArea className="min-h-[200px] max-h-[min(50vh,420px)] rounded-lg border border-border bg-background/80 dark:bg-card/40">
              <div className="p-4 pr-5">
                {coachLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Coach wird geladen…
                  </div>
                ) : coachMd ? (
                  <MarkdownText content={coachMd} className="prose prose-sm dark:prose-invert max-w-none" />
                ) : (
                  <p className="text-sm text-muted">Keine Ausgabe.</p>
                )}
              </div>
            </ScrollArea>

            <div className="flex flex-col gap-2 rounded-lg border border-border p-3">
              <Label className="text-xs text-muted">Zusatz für den Coach (optional)</Label>
              <Textarea
                value={extraNote}
                onChange={(e) => setExtraNote(e.target.value)}
                placeholder="z. B. Fokus DACH, Budget knapp …"
                rows={5}
                className="min-h-[120px] resize-y text-sm"
              />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="w-full"
                onClick={() => void loadCoach(true)}
                disabled={coachLoading}
              >
                {coachLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Coach neu laden'}
              </Button>
              {stepIndex === 2 && doneCount < 1 && (
                <p className="text-[11px] leading-snug text-warning">
                  Für den nächsten Schritt brauchst du mindestens einen abgeschlossenen Research-Run.
                </p>
              )}
              {mainTab === 'research' && stepIndex < 3 && (
                <p className="text-[11px] text-muted">
                  Tab «Forschung» ist aktiv — unten kannst du Anfragen starten.
                </p>
              )}
              {mainTab === 'strategy' && stepIndex === 3 && (
                <p className="text-[11px] text-muted">
                  Tab «Strategie» — KI-Generierung und Textfelder.
                </p>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="shrink-0 flex-col gap-2 border-t border-border px-6 py-4 sm:flex-row sm:justify-between">
          <div className="flex w-full flex-wrap gap-2 sm:w-auto">
            <Button type="button" variant="outline" onClick={goBack} disabled={stepIndex === 0}>
              <ChevronLeft className="h-4 w-4" />
              Zurück
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={openCompanion}
              className="gap-2"
            >
              <Bot className="h-4 w-4" />
              Im Companion fortsetzen
            </Button>
          </div>
          <div className="flex w-full justify-end gap-2 sm:w-auto">
            {stepIndex < STEP_IDS.length - 1 ? (
              <Button type="button" onClick={goNext} disabled={!canGoNext} className="gap-1">
                Weiter
                <ChevronRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button type="button" onClick={() => onOpenChange(false)}>
                Fertig
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
